import { inject, Injectable } from '@angular/core';
import { EventTimeline, MatrixClient, MatrixEvent, TimelineWindow } from 'matrix-js-sdk';
import { concatMap, from, Observable } from 'rxjs';
import { DialogService } from '../dialog/dialog.service';
import { DialogData } from '../../common/components/dialog/dialog.component';
import { DisplayMessage } from '../../components/sb-client/sb-model/display-message';
import { MatrixService } from '../matrix/matrix.service';
import { ChatStoreService } from '../chat-store/chat-store.service';
import { ChatMessageService } from '../chat-message/chat-message.service';
import { DisplayItem, DisplayItemType } from '../../components/sb-client/sb-model/display-item';
import { DisplayInfo } from '../../components/sb-client/sb-model/display-info';
import { Room as ModelRoom } from '../../model/room';
import { RoomHelperService } from '../room/room-helper.service';
import { DisplayMessageFile } from '../../components/sb-client/sb-model/display-message-file';
import { ArchiveTestIds } from './archive.test-ids';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Injectable({
  providedIn: 'root'
})
/**
 * Diese Serviceklasse kapselt alle Methoden, die zum Ausführen des Archivierungsprozesses notwendig sind.
 *
 * Hierdurch erfüllte AFO's:
 *  A_25424 - Archivierung in Archivsysteme
 *  A_23130 - Nutzung von Daten durch Drittsysteme
 */
export class ArchiveService {
  private readonly clientService = inject(MatrixService);
  private readonly dialogService = inject(DialogService);
  private readonly chatStoreService = inject(ChatStoreService);
  private readonly chatMessageService = inject(ChatMessageService);

  /**
   * Prüft zuerst, ob wirklich archiviert werden soll. Ist dies bestätigt, wird die Archivierung gestartet.
   * @param roomId
   */
  public archiving(roomId: string): Observable<Blob | null | void> {
    return this.showArchiveDialog([roomId]).pipe(
      concatMap(answer => {
        if (!answer) {
          // Wenn der Benutzer nicht zustimmt, sofort eine abgeschlossene Observable zurückgeben
          throw new Error('Archivierung abgebrochen.');
        }
        return from(this.createArchivePdf(roomId));
      })
    );
  }

  public async createArchivePdf(roomId: string): Promise<Blob> {
    const client = this.clientService.client;
    const events = await this.loadAllEvents(client, roomId);

    const chatroom = this.chatStoreService.roomEventsToChatRoom(events);
    const room = client.getRoom(roomId);
    const displayMessages = await this.chatMessageService.roomToDisplayItemList(chatroom, client);

    const blocks: PdfBlock[] = [];

    blocks.push({ type: 'divider', content: '' });

    const messageLines = this.transformEvents(displayMessages);

    blocks.push(
      {
        type: 'title',
        content: `Chat: ${room?.name ?? 'Unbekannter Chat'}`
      },
      { type: 'divider', content: '' },
      {
        type: 'subtitle',
        content: 'Chatteilnehmer'
      },
      {
        type: 'table',
        table: {
          head: ['Anzeigename', 'Matrix-ID'],
          body: (room?.getMembers() ?? []).map(member => [member.rawDisplayName ?? '-', member.userId])
        }
      },
      { type: 'divider', content: '' },
      ...messageLines.map(
        line =>
          ({
            type: 'text',
            content: line
          }) as const
      )
    );

    return this.createPdf(blocks);
  }

  asDisplayMessage = (di: DisplayItem): DisplayMessage | undefined => (di instanceof DisplayMessage ? di : undefined);

  asDisplayMessageFile = (di: DisplayItem): DisplayMessageFile | undefined =>
    di instanceof DisplayMessageFile ? di : undefined;

  asDisplayInfo = (di: DisplayItem): DisplayInfo | undefined => (di instanceof DisplayInfo ? di : undefined);

  handleArchivingResponse(room: ModelRoom, response: void | Blob | null): void {
    if (response instanceof Blob) {
      RoomHelperService.downloadFile(room.name + '_archive.pdf', response);
    }
  }

  /**
   * Zum Anzeigen des Dialogs zur Archivierung, aber OHNE die Archivierung durchzuführen.
   * Der Dialog passt die Formulierung entsprechend an, je nachdem ob nur einer oder meherere Röume archiviert werden.
   * @returns Wahrheitswert, ob Dialog bestätigt wurde.
   */
  public showArchiveDialog(roomIds: string[]): Observable<boolean> {
    const rooms = roomIds.map(roomId => this.clientService.client.getRoom(roomId));
    let message;
    if (rooms.length <= 1) {
      message =
        'Bei der Archivierung wird der Chatverlauf an Drittsysteme übermittelt, sind Sie sich sicher, dass Sie den ' +
        `Chatverlauf für den Chat ${rooms[0]?.name} archivieren wollen?`;
    } else {
      message =
        'Bei der Archivierung wird der Chatverlauf an Drittsysteme übermittelt, sind Sie sich sicher, dass Sie den ' +
        `Chatverlauf für die Räume ${rooms.map(room => room?.name).join(', ')} archivieren wollen?`;
    }

    const archivingDialogData: DialogData = {
      title: 'Chat Archivieren',
      message: message,
      confirmText: 'Chat Archivieren',
      cancelText: 'Abbrechen',
      svgIcon: 'circle-warning',
      cancelDataTestId: ArchiveTestIds.buttonCancelArchiveroom,
      confirmDataTestId: ArchiveTestIds.buttonConfirmArchiveroom,
      dialogContentTestId: ArchiveTestIds.dialogContentArchiveroom,
      dialogActionTestId: ArchiveTestIds.dialogActionsArchiveroom
    };

    return this.dialogService.openWarningDialog(archivingDialogData, '28.6rem', '21.8rem');
  }

  private createTimestamp(localTimestamp: number | Date): string {
    let date;
    if (localTimestamp instanceof Date) {
      date = localTimestamp;
    } else {
      date = new Date(localTimestamp);
    }

    const pad = (n: number): string => n.toString().padStart(2, '0');

    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} - ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  private stripHtml(html: string | undefined | null): string {
    if (!html) {
      return '';
    }

    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(html, 'text/html');

    return parsedDocument.body.textContent ?? '';
  }

  private transformEvents(displayItems: DisplayItem[]): string[] {
    return displayItems
      .map(displayItem => {
        switch (displayItem.type) {
          case DisplayItemType.INFO: {
            const displayinfo: DisplayInfo | undefined = this.asDisplayInfo(displayItem);
            if (!displayinfo) {
              return null;
            }
            return this.createTimestamp(displayinfo.timestamp) + ' - ' + displayinfo.infotext;
          }
          case DisplayItemType.MESSAGE: {
            const displaymessage: DisplayMessage | undefined = this.asDisplayMessage(displayItem);
            if (!displaymessage) {
              return null;
            }
            if (displaymessage.content['msgtype'] === 'm.bad.encrypted') {
              return displaymessage?.body;
            }
            return (
              this.createTimestamp(displaymessage.date!) +
              ' - ' +
              displaymessage.sender +
              ': ' +
              this.stripHtml(displaymessage.formattedBody).replaceAll(/[^À-ÿĀ-žẞ\x20-\x7E\n\r]/gu, '')
            );
          }
          case DisplayItemType.FILEMESSAGE: {
            const displaymessageFile: DisplayMessageFile | undefined = this.asDisplayMessageFile(displayItem);

            if (!displaymessageFile) {
              return null;
            }
            const stripHtml = this.stripHtml(displaymessageFile.formattedBody).replaceAll(
              /[^À-ÿĀ-žẞ\x20-\x7E\n\r]/gu,
              ''
            );
            return (
              this.createTimestamp(displaymessageFile.date!) +
              ' - ' +
              displaymessageFile.sender +
              ': ' +
              'File:' +
              displaymessageFile.name +
              (stripHtml.trim() === '' ? '' : ' Text: ' + stripHtml)
            );
          }
        }
      })
      .filter(it => it !== null && it !== undefined);
  }

  private createPdf(blocks: PdfBlock[]): Blob {
    const doc = new jsPDF();

    const pageHeight = 280;
    let yPosition = 15;

    const addPageIfNeeded = (lineHeight: number): void => {
      if (yPosition + lineHeight > pageHeight) {
        doc.addPage();
        yPosition = 15;
      }
    };

    blocks.forEach(block => {
      switch (block.type) {
        case 'title':
          doc.setFontSize(14);
          doc.setFont('arial', 'bold');

          addPageIfNeeded(10);
          doc.text(block.content ?? '', 10, yPosition);
          yPosition += 10;
          break;

        case 'subtitle':
          doc.setFontSize(10);
          doc.setFont('arial', 'bold');

          addPageIfNeeded(8);
          doc.text(block.content ?? '', 10, yPosition);
          yPosition += 8;
          break;

        case 'divider':
          addPageIfNeeded(6);
          doc.setDrawColor(180);
          doc.line(10, yPosition, 200, yPosition);
          yPosition += 6;
          break;

        case 'table':
          autoTable(doc, {
            startY: yPosition,
            head: [block.table?.head ?? []],
            body: block.table?.body ?? [],
            styles: {
              fontSize: 7,
              cellPadding: 2
            },
            margin: {
              left: 10
            },
            headStyles: {
              fillColor: [230, 230, 230],
              textColor: 0,
              fontStyle: 'bold'
            },
            columnStyles: {
              0: { cellWidth: 70 },
              1: { cellWidth: 120 }
            }
          });

          yPosition = (doc as any).lastAutoTable.finalY + 8;
          break;

        case 'text':
        default: {
          doc.setFontSize(7);
          doc.setFont('arial', 'normal');

          const splitText = doc.splitTextToSize(block.content ?? '', 180) as string[];
          splitText.forEach(line => {
            addPageIfNeeded(5);
            doc.text(line, 10, yPosition);
            yPosition += 5;
          });
          break;
        }
      }
    });

    return doc.output('blob');
  }

  /**
   * Lädt alle Events eines Raums
   */
  private async loadAllEvents(client: MatrixClient, roomId: string): Promise<MatrixEvent[]> {
    const room = client.getRoom(roomId);
    if (!room) {
      throw new Error(`Raum ${roomId} nicht gefunden.`);
    }

    // 1. TimelineWindow initialisieren
    const timelineSet = room.getTimelineSets()[0];
    const timelineWindow = new TimelineWindow(client, timelineSet);

    // 2. Initial laden (live timeline)
    await timelineWindow.load(); // lädt z. B. 20 neueste Events

    // 3. Rückwärts paginieren, bis keine Events mehr da sind
    while (timelineWindow.canPaginate(EventTimeline.BACKWARDS)) {
      await timelineWindow.paginate(EventTimeline.BACKWARDS, 100); // 100 pro Chunk
    }

    return timelineWindow.getEvents(); // ALLE geladenen Events (alt → neu)
  }
}

type PdfBlockType = 'title' | 'subtitle' | 'text' | 'divider' | 'table';

interface PdfBlock {
  type: PdfBlockType;
  content?: string;
  table?: {
    head: string[];
    body: string[][];
  };
}
